import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  DocumentTextIcon, 
  ArrowTopRightOnSquareIcon, 
  EyeIcon, 
  ShieldCheckIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import { getSCGRuleSourceInfo } from '../services/api';

const SCGCitation = ({ ruleId, compact = false }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);

  // Fetch SCG rule source information
  const { data: sourceInfo, isLoading, error } = useQuery({
    queryKey: ['scg-source', ruleId],
    queryFn: () => getSCGRuleSourceInfo(ruleId),
    enabled: !!ruleId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Open PDF viewer when citation is clicked
  useEffect(() => {
    if (showPdfViewer && sourceInfo?.source_available && sourceInfo?.citation_display?.pdf_url) {
      const pdfUrl = sourceInfo.citation_display.pdf_url;
      const page = sourceInfo?.location_metadata?.page_number || 1;
      
      // Build URL parameters for the PDF viewer
      const params = new URLSearchParams({
        pdf: pdfUrl,
        page: page.toString()
      });
      
      // Open in new window with PDF viewer - use current frontend origin for cross-deployment compatibility
      const currentOrigin = window.location.origin; // Gets http://localhost:3001 or http://localhost:3000 or production URL
      const viewerUrl = `${currentOrigin}/pdf-viewer?${params.toString()}`;
      window.open(viewerUrl, '_blank', 'width=1200,height=800');
      
      // Reset state after opening
      setShowPdfViewer(false);
    }
  }, [showPdfViewer, sourceInfo?.source_available]);

  const openPdfViewer = () => {
    if (sourceInfo?.source_available && sourceInfo?.citation_display?.pdf_url) {
      setShowPdfViewer(true);
    } else if (sourceInfo?.citation_display?.pdf_url) {
      // Fallback: open in new tab if source not available
      window.open(sourceInfo.citation_display.pdf_url, '_blank');
    }
  };

  const copyRuleId = () => {
    navigator.clipboard.writeText(ruleId);
  };

  if (isLoading) {
    return (
      <span className="inline-flex items-center text-gray-500 text-sm">
        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 mr-1"></div>
        {ruleId}
      </span>
    );
  }

  if (error || !sourceInfo) {
    return (
      <span className="inline-flex items-center text-red-600 font-mono text-sm">
        <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
        {ruleId}
      </span>
    );
  }

  if (compact) {
    return (
      <button
        onClick={sourceInfo.source_available ? openPdfViewer : copyRuleId}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`relative inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors ${
          sourceInfo.source_available
            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
        }`}
        title={sourceInfo.source_available ? 'Click to view source document' : 'Source document not available'}
      >
        {sourceInfo.source_available ? (
          <DocumentTextIcon className="w-3 h-3 mr-1" />
        ) : (
          <ShieldCheckIcon className="w-3 h-3 mr-1" />
        )}
        {ruleId}
        {sourceInfo.source_available && <ArrowTopRightOnSquareIcon className="w-3 h-3 ml-1" />}
        
        {/* Tooltip */}
        {showTooltip && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-50 whitespace-nowrap">
            {sourceInfo.citation_display?.friendly_reference || sourceInfo.rule_details?.item || ruleId}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
          </div>
        )}
      </button>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-2">
          <ShieldCheckIcon className="w-5 h-5 text-blue-600" />
          <div>
            <h4 className="text-sm font-medium text-gray-900">
              {sourceInfo.citation_display?.friendly_reference || 'SCG Rule'}
            </h4>
            <p className="text-xs text-gray-500 font-mono">{ruleId}</p>
          </div>
        </div>
        
        {/* Classification Level Badge */}
        {sourceInfo.rule_details?.classification_level && (
          <span className={`px-2 py-1 text-xs font-medium rounded ${
            sourceInfo.rule_details.classification_level === 'TOP SECRET' ? 'bg-red-100 text-red-800' :
            sourceInfo.rule_details.classification_level === 'SECRET' ? 'bg-orange-100 text-orange-800' :
            sourceInfo.rule_details.classification_level === 'CONFIDENTIAL' ? 'bg-yellow-100 text-yellow-800' :
            'bg-green-100 text-green-800'
          }`}>
            {sourceInfo.rule_details.classification_level}
          </span>
        )}
      </div>

      {/* Enhanced Description */}
      {sourceInfo.rule_details?.enhanced_description && (
        <p className="text-sm text-gray-700">
          {sourceInfo.rule_details.enhanced_description}
        </p>
      )}

      {/* Context Information */}
      {(sourceInfo.rule_details?.context_agency || sourceInfo.rule_details?.context_section) && (
        <div className="flex flex-wrap gap-2 text-xs">
          {sourceInfo.rule_details.context_agency && (
            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
              {sourceInfo.rule_details.context_agency}
            </span>
          )}
          {sourceInfo.rule_details.context_section && (
            <span className="px-2 py-1 bg-gray-50 text-gray-700 rounded">
              {sourceInfo.rule_details.context_section}
            </span>
          )}
        </div>
      )}

      {/* Source Document Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          {sourceInfo.source_available ? (
            <>
              <DocumentTextIcon className="w-4 h-4 inline mr-1" />
              {sourceInfo.citation_display?.page_reference || 'Source Available'}
            </>
          ) : (
            'Source document not available'
          )}
        </div>
        
        <div className="flex space-x-2">
          {sourceInfo.source_available && (
            <button
              onClick={openPdfViewer}
              className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
            >
              <EyeIcon className="w-3 h-3 mr-1" />
              View Source
            </button>
          )}
          
          <button
            onClick={copyRuleId}
            className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition-colors"
          >
            Copy ID
          </button>
        </div>
      </div>

    </div>
  );
};

export default SCGCitation;